import {
  runCopyToWorkspaceWorkflow,
  runPublishFileRecordWorkflow,
  runShareFileRecordWorkflow,
} from "@/action-dialog";
import { useToggleFavorite } from "@/api/resourceTags";
import type { ActionScope, ObjectActionHandlers } from "@/object-actions/types";
import { resolveFileRecordShowPublish } from "@/panes/file-record-detail/publish";
import { RESOURCE_TAG_FAVORITE, type FileRecord } from "@cohesive/api-client";
import { useMemo } from "react";
import toast from "react-hot-toast";

async function copyFileRecordUrlToClipboard(url: string): Promise<void> {
  await navigator.clipboard.writeText(url);
  toast.success("URL copied to clipboard");
}

/**
 * Public viewer URL for a published file, or undefined when the file has
 * never been published. The backend stores the raw CloudFront URL
 * (`{host}/public/{id}`); the user-facing viewer lives under
 * `/view/public/{id}`, so we inject `/view` here — mirrors FilePublishButton.
 */
function resolveFileRecordPublicUrl(record: FileRecord): string | undefined {
  if (!record.published || !record.published_url) return undefined;
  return record.published_url.replace("/public/", "/view/public/");
}

export interface FileRecordActionHandlerDeps {
  onOpen?: (record: FileRecord) => void;
  onRename?: (record: FileRecord) => void;
  onMove?: (record: FileRecord) => void;
  onCopy?: (record: FileRecord) => void;
  onDelete?: (record: FileRecord) => void;
  /** Hide the file's canvas node (non-destructive; sets `node.hidden`). */
  onHideOnCanvas?: (record: FileRecord) => void;
  /** Un-hide the file's canvas node. */
  onShowOnCanvas?: (record: FileRecord) => void;
  /** Registry scope — gates publish on shared-with-me / favorites. */
  actionScope?: ActionScope;
}

export function createFileRecordActionHandlers(
  record: FileRecord,
  deps: FileRecordActionHandlerDeps,
  toggleFavorite: ReturnType<typeof useToggleFavorite>,
): ObjectActionHandlers {
  const isFavorite = !!record.tags?.includes(RESOURCE_TAG_FAVORITE);

  return {
    star:
      record.id && !isFavorite
        ? () =>
            toggleFavorite.mutate({
              resourceType: "file-record",
              resourceId: record.id,
              isFavorite,
            })
        : undefined,
    unstar:
      record.id && isFavorite
        ? () =>
            toggleFavorite.mutate({
              resourceType: "file-record",
              resourceId: record.id,
              isFavorite,
            })
        : undefined,
    open: deps.onOpen ? () => deps.onOpen?.(record) : undefined,
    rename: deps.onRename
      ? () => window.setTimeout(() => deps.onRename?.(record), 0)
      : undefined,
    move: deps.onMove
      ? () => window.setTimeout(() => deps.onMove?.(record), 0)
      : undefined,
    duplicate: deps.onCopy ? () => deps.onCopy?.(record) : undefined,
    share: record.id
      ? () =>
          window.setTimeout(
            () => void runShareFileRecordWorkflow({ record }),
            0,
          )
      : undefined,
    "copy-url": (() => {
      const publicUrl = resolveFileRecordPublicUrl(record);
      return publicUrl
        ? () => {
            void copyFileRecordUrlToClipboard(publicUrl).catch(() => {
              toast.error("Failed to copy URL");
            });
          }
        : undefined;
    })(),
    publish:
      record.id &&
      resolveFileRecordShowPublish({
        record,
        canEdit: record.can_edit !== false,
        scope: deps.actionScope,
      })
        ? () =>
            window.setTimeout(
              () => void runPublishFileRecordWorkflow({ record }),
              0,
            )
        : undefined,
    download:
      record.status === "processed" && !record.path?.endsWith("/")
        ? () => {}
        : undefined,
    "copy-to-workspace": () =>
      window.setTimeout(() => void runCopyToWorkspaceWorkflow({ record }), 0),
    delete: deps.onDelete ? () => deps.onDelete?.(record) : undefined,
    "remove-from-canvas": deps.onHideOnCanvas
      ? () => deps.onHideOnCanvas?.(record)
      : undefined,
    "show-on-canvas": deps.onShowOnCanvas
      ? () => deps.onShowOnCanvas?.(record)
      : undefined,
  };
}

export function useFileRecordObjectActionHandlers(
  record: FileRecord,
  deps: FileRecordActionHandlerDeps,
): ObjectActionHandlers {
  const toggleFavorite = useToggleFavorite();
  return useMemo(
    () => createFileRecordActionHandlers(record, deps, toggleFavorite),
    [deps, record, toggleFavorite],
  );
}
